export type AccessState = 'ok' | 'grace' | 'blocked';

export interface SubscriptionInfo {
  status: string;
  plan: string;
  billingPeriod: 'MONTHLY' | 'YEARLY';
  amountKopecks: number;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  accessEndsAt: string | null;
  daysLeft: number | null;
  accessState: AccessState;
  graceDaysLeft: number;
}

/**
 * Точка сети владельца в справке карточки. У каждой точки своя подписка и
 * оплата; `mine` — привязана ли она к этому же менеджеру (тогда есть карточка).
 */
export interface NetworkPoint {
  id: string;
  name: string;
  mine: boolean;
  /** Карточку можно открыть: своя точка или смотрит главный менеджер. */
  canOpen?: boolean;
  status: string;
  accessEndsAt: string | null;
  daysLeft: number | null;
  accessState: AccessState | null;
}

export interface OwnerNetwork {
  ownerEstablishmentCount: number;
  establishments: NetworkPoint[];
}

export function pluralEstablishments(n: number): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return 'заведений';
  if (last > 1 && last < 5) return 'заведения';
  if (last === 1) return 'заведение';
  return 'заведений';
}

const STATUS_LABELS: Record<string, string> = {
  trial: 'Триал',
  active: 'Активна',
  past_due: 'Просрочена',
  cancelled: 'Отменена',
  paused: 'Приостановлена',
};

/**
 * Что показать на плашке. Статус в базе меняет ночной cron, поэтому у точки с
 * закончившимся периодом он ещё может быть «active» — берём фактическое
 * состояние доступа, иначе плашка спорит с колонкой «Осталось».
 */
export function statusLabel(sub: SubscriptionInfo): string {
  if (sub.accessState !== 'ok') return 'Просрочена';
  return STATUS_LABELS[sub.status] ?? sub.status;
}

/** Цвет плашки статуса: горящее — красным, триал — жёлтым, оплаченное — зелёным. */
export function statusTone(sub: SubscriptionInfo): 'green' | 'amber' | 'red' | 'slate' {
  if (sub.accessState === 'blocked') return 'red';
  if (sub.accessState === 'grace') return 'red';
  if (sub.status === 'trial') return 'amber';
  if (sub.status === 'active') {
    return sub.daysLeft != null && sub.daysLeft <= 3 ? 'amber' : 'green';
  }
  if (sub.status === 'past_due') return 'red';
  return 'slate';
}

const PLURALS: [string, string, string] = ['день', 'дня', 'дней'];

export function pluralDays(n: number): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return PLURALS[2];
  if (last > 1 && last < 5) return PLURALS[1];
  if (last === 1) return PLURALS[0];
  return PLURALS[2];
}

/** Плашка точки сети — по её собственному статусу и состоянию доступа. */
export function networkPointTone(p: NetworkPoint): 'green' | 'amber' | 'red' | 'slate' {
  if (p.status === 'none') return 'slate';
  if (p.accessState === 'blocked' || p.accessState === 'grace') return 'red';
  if (p.status === 'trial') return 'amber';
  if (p.status === 'active') return p.daysLeft != null && p.daysLeft <= 3 ? 'amber' : 'green';
  if (p.status === 'past_due') return 'red';
  return 'slate';
}

export function networkPointLabel(p: NetworkPoint): string {
  if (p.status === 'none') return 'Нет подписки';
  if (p.accessState && p.accessState !== 'ok') return 'Просрочена';
  return STATUS_LABELS[p.status] ?? p.status;
}

/** Человеческий остаток срока: «12 дней», «догоняющие 2 дня», «доступ закрыт». */
export function daysLeftLabel(sub: SubscriptionInfo | null): string {
  if (!sub) return '—';
  if (sub.accessState === 'blocked') return 'Доступ закрыт';
  if (sub.accessState === 'grace') {
    return `Просрочено, ещё ${sub.graceDaysLeft} ${pluralDays(sub.graceDaysLeft)}`;
  }
  if (sub.daysLeft == null) return 'Без срока';
  if (sub.daysLeft === 0) return 'Заканчивается сегодня';
  return `${sub.daysLeft} ${pluralDays(sub.daysLeft)}`;
}
